"use client";

import { Box, Divider, Slider, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import MinimizeIcon from "../../../../assets/images/icons/MinimizeIcon";
import PlusIcon from "../../../../assets/images/icons/PlusIcon";
import ResetLayer from "../../../../assets/images/icons/ResetLayer";
import BriaButton from "../../../../components/common/BriaButton/BriaButton";
import BriaInput from "../../../../components/common/BriaInput/BriaInput";
import BriaSlider from "../../../../components/common/BriaSlider/BriaSlider";
import { DEFAULT_BODY_INTENSITY, DEFAULT_WINDOWS_INTENSITY } from "../../../../constants/AutomotiveConstants";
import { useAppStore } from "../../../../hooks/useStores";
import styles from "./ReflectionControls.module.scss";

type ReflectionControlsProps = {
	onIntensityChange: (layerName: string, intensity: number) => void;
};

const ReflectionControls = ({ onIntensityChange }: ReflectionControlsProps) => {
	const { t } = useTranslation("translation", {
		keyPrefix: "playground.automotive.config.features",
	});

	const { playgroundStore, imageToImageStore } = useAppStore();
	const { projectsStore, automotiveStore } = playgroundStore;
	const { reflectionStore } = automotiveStore;
	const {
		singleSelectedImage,
		imagesReflections,
		selectedReflectionLayer,
		imagesEffects,
		imagesHarmonizations,
		selectedSegmentation,
	} = playgroundStore;

	const zoom = reflectionStore.reflectionZoom;
	const setZoom = reflectionStore.setReflectionZoom;
	const magnifierEnabled = reflectionStore.magnifierEnabled;
	const setMagnifierEnabled = reflectionStore.setMagnifierEnabled;
	const magnifierZoom = reflectionStore.magnifierZoom;
	const setMagnifierZoom = reflectionStore.setMagnifierZoom;
	const magnifierSize = reflectionStore.magnifierSize;
	const setMagnifierSize = reflectionStore.setMagnifierSize;
	const enableZoom = false;

	const selectedImageReflections = projectsStore.isAutomotiveReflectionConfigEnabled
		? imagesReflections?.find((reflection) => reflection.url === singleSelectedImage?.url)
		: projectsStore.isAutomotiveEffectsConfigEnabled
		? imagesEffects?.find((reflection) => reflection.url === singleSelectedImage?.url)
		: imagesHarmonizations?.find((reflection) => reflection.url === singleSelectedImage?.url);

	const selectedLayer =
		projectsStore.isAutomotiveEffectsConfigEnabled || projectsStore.isAutomotiveHarmonizationConfigEnabled
			? selectedImageReflections?.layers[0]
			: selectedImageReflections?.layers.find((layer) => layer.name === selectedReflectionLayer?.name);

	if (!selectedLayer && !selectedSegmentation) return null;
	const renderLayerControls = () => {
		if (!selectedLayer) return null;
		return (
			<>
				<Typography className={styles.label}>
					{selectedLayer.name === "effects"
						? t("effects.EffectsStrength")
						: selectedLayer.name === "harmonization"
						? t("harmonization.harmonizationDepth")
						: `${t(`reflection.layers.${selectedLayer?.name}`)} ${t("reflection.opacity")}`}
				</Typography>
				<Box className={styles.sliderWrapper}>
					<Slider
						value={selectedLayer?.intensity}
						min={0}
						max={1}
						step={0.01}
						onChange={(_, value) => {
							onIntensityChange(selectedLayer.name, value as number);
						}}
						valueLabelDisplay="auto"
						valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
						className={styles.slider}
					/>
				</Box>
				<Divider className={styles.divider} />

				<BriaButton
					startIcon={<ResetLayer />}
					buttonType="textMedium"
					size="small"
					className={styles.resetBtn}
					onClick={() =>
						onIntensityChange(
							selectedLayer.name,
							selectedLayer.name === "body" ? DEFAULT_BODY_INTENSITY : DEFAULT_WINDOWS_INTENSITY,
						)
					}
				>
					{t("reflection.resetLayer")}
				</BriaButton>
			</>
		);
	};

	const renderSegmentationsControls = () => {
		if (!selectedSegmentation) return null;
		return (
			<>
				<Typography className={styles.label}>{t("reflection.brushSize")}</Typography>
				<Box className={styles.sliderWrapper}>
					<BriaSlider
						value={imageToImageStore.brushConfigs.lineWidth}
						onChange={(e: any) => {
							imageToImageStore.brushConfigs.lineWidth = Number(e.target.value ?? 0);
						}}
						min={0}
						max={100}
						valueLabelDisplay={"auto"}
						className={styles.slider}
					/>
				</Box>
				<Divider orientation="horizontal" className={styles.divider} />
				<BriaButton
					startIcon={<ResetLayer />}
					buttonType="textMedium"
					size="small"
					className={styles.resetBtn}
					onClick={async () => {
						reflectionStore.handleResetBrusher();
						imageToImageStore.toggleIsMaskApplied(false);

						const segmentationName = selectedSegmentation?.name;
						const canvas = reflectionStore.selectedSegmentationRef?.current?.get(segmentationName);
						if (canvas) {
							const ctx = canvas.getContext("2d");
							if (ctx) {
								ctx.clearRect(0, 0, canvas.width, canvas.height);
							}
						}
					}}
				>
					{t("reflection.clearMask")}
				</BriaButton>

				<Divider orientation="horizontal" className={styles.divider} />

				<Box className={styles.toggleWrapper}>
					<BriaButton
						buttonType={magnifierEnabled ? "primaryMedium" : "secondaryMedium"}
						size="small"
						onClick={() => setMagnifierEnabled(!magnifierEnabled)}
						className={styles.toggleButton}
					>
						{t("reflection.magnifier")}
					</BriaButton>
				</Box>

				{magnifierEnabled && (
					<>
						<Box className={styles.zoomControls}>
							<BriaButton
								size="small"
								onClick={() => setMagnifierZoom(Math.max(magnifierZoom - 0.5, 1.5))}
							>
								<MinimizeIcon fillColor="var(--black-color)" />
							</BriaButton>

							<Box className={styles.zoomInputWrapper}>
								<BriaInput
									className={styles.zoomInput}
									value={`${Math.round(magnifierZoom * 100)}`}
									onChange={(e) => {
										const value = Number(e.target.value);
										if (!isNaN(value)) {
											setMagnifierZoom(Math.min(Math.max(value / 100, 1.5), 10)); // Clamp between 150% and 1000%
										}
									}}
									onBlur={(e) => {
										const value = Number(e.target.value);
										if (isNaN(value) || value < 150) {
											setMagnifierZoom(1.5);
										}
									}}
								/>

								<Typography className={styles.percentSymbol}>%</Typography>
							</Box>

							<BriaButton
								size="small"
								onClick={() => setMagnifierZoom(Math.min(magnifierZoom + 0.5, 10))}
							>
								<PlusIcon fillColor="var(--black-color)" />
							</BriaButton>
						</Box>

						<Box className={styles.sliderWrapper}>
							<Slider
								value={magnifierSize}
								min={100}
								max={300}
								step={10}
								onChange={(_, value) => {
									setMagnifierSize(value as number);
								}}
								valueLabelDisplay="auto"
								className={styles.slider}
							/>
						</Box>
					</>
				)}
				{enableZoom && (
					<>
						<Divider orientation="horizontal" className={styles.divider} />
						<Box className={styles.zoomControls}>
							<BriaButton size="small" onClick={() => setZoom(Math.max(zoom - 0.01, 0.01))}>
								<MinimizeIcon fillColor="var(--black-color)" />
							</BriaButton>

							<Box className={styles.zoomInputWrapper}>
								<BriaInput
									className={styles.zoomInput}
									value={`${Math.round(zoom * 100)}`}
									onChange={(e) => {
										const value = Number(e.target.value);
										if (!isNaN(value)) {
											setZoom(Math.min(Math.max(value / 100, 0.01), 5)); // Clamp between 1% and 500%
										}
									}}
									onBlur={(e) => {
										const value = Number(e.target.value);
										if (isNaN(value) || value < 1) {
											setZoom(0.01);
										}
									}}
								/>

								<Typography className={styles.percentSymbol}>%</Typography>
							</Box>

							<BriaButton size="small" onClick={() => setZoom(Math.min(zoom + 0.01, 5))}>
								<PlusIcon fillColor="var(--black-color)" />
							</BriaButton>
						</Box>
					</>
				)}
			</>
		);
	};

	return (
		<Box className={styles.controlsContainer}>
			{selectedLayer && renderLayerControls()}
			{selectedSegmentation && renderSegmentationsControls()}
		</Box>
	);
};

export default observer(ReflectionControls);
